import type { EVoxRoles } from 'providers/declare';

import { getEnvironment } from 'utils/getEnvironment';

export interface IUXFProperties {
    environment: ReturnType<typeof getEnvironment>;
    meeting_id: string;
    meeting_user_role: EVoxRoles;
    user_id: string;
}

type TEvent = keyof UXFManager['config']['events'];

declare global {
    interface Window {
        UXS?: {
            addProperties: (properties: IUXFProperties) => void;
            sendEvent: (eventName: string) => void;
        };
    }
}
// https://uxfeedback.atlassian.net/wiki/spaces/UXFBBZ/pages/154337825/Widget.js
class UXFManager {
    private stash: Map<TEvent, IUXFProperties> = new Map();

    private environment: keyof UXFManager['config']['events'][TEvent];

    private config = {
        events: {
            CALL_ENDED_CLIENT: {
                dev: '18150',
                prod: '18137',
            },
            CALL_ENDED_MANAGER: {
                dev: '18138',
                prod: '18149',
            },
        },
        storageName: 'UXFParameters',
    };

    private api: typeof window.UXS;

    constructor() {
        this.environment = getEnvironment() === 'prod' ? 'prod' : 'dev';
        this.checkUXFAvailability();
    }

    private checkUXFAvailability() {
        document.addEventListener('uxsReady', () => {
            this.api = window.UXS;
            this.sendStashedEvents().emptyStash();
        });

        return this;
    }

    private emptyStash() {
        this.stash = new Map();

        return this;
    }

    private sendStashedEvents() {
        this.stash.forEach((properties, eventName) => {
            this.sendEvent(eventName, properties);
        });

        return this;
    }

    private stashEvent(eventName: TEvent, properties: IUXFProperties) {
        this.stash.set(eventName, properties);

        return this;
    }

    public setProperties(properties: IUXFProperties) {
        if (this.api) this.api.addProperties(properties);

        window.sessionStorage.setItem(this.config.storageName, JSON.stringify(properties));

        return this;
    }

    public sendEvent(eventName: TEvent, properties: IUXFProperties) {
        if (this.api) {
            this.setProperties(properties);
            this.api.sendEvent(this.config.events[eventName][this.environment]);
        } else {
            this.stashEvent(eventName, properties);
        }

        return this;
    }

    public getStoredProperties(): IUXFProperties | null {
        return JSON.parse(window.sessionStorage.getItem(this.config.storageName) ?? 'null');
    }
}

export const UXF = new UXFManager();
